import type { LocationTreeNode, UpdateLocationPayload } from '../api/schedulingApis';

/**
 * Return the given ids sorted by node depth descending (deepest first).
 * Uses location_level from the tree; ids not found in the tree are placed at the end.
 */
export function getIdsSortedByDepthDescending(
  nodes: LocationTreeNode[],
  idSet: Set<string>
): string[] {
  const list: { id: string; level: number }[] = [];
  function walk(listNodes: LocationTreeNode[]) {
    for (const n of listNodes) {
      const id = String(n.location_id);
      if (idSet.has(id)) list.push({ id, level: n.location_level ?? 0 });
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  list.sort((a, b) => b.level - a.level);
  return list.map((x) => x.id);
}

/** Flatten tree to UpdateLocationPayload[] for updateLocation API. */
export function flattenTreeToUpdatePayloads(
  nodes: LocationTreeNode[],
  projectId: string
): UpdateLocationPayload[] {
  const out: UpdateLocationPayload[] = [];
  function walk(list: LocationTreeNode[]) {
    for (const n of list) {
      out.push({
        id: n.location_id,
        projectId,
        name: n.name ?? '',
        locationType: n.location_type ?? 'project',
        locationLevel: n.location_level ?? 1,
        parentLocationId: n.parent_location_id ?? null,
        locationGroup: Array.isArray(n.location_group) ? n.location_group : [],
        meta: n.meta ?? {},
      });
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  return out;
}

/** Collect node id and all descendant ids. */
export function collectDescendantIds(nodes: LocationTreeNode[]): Set<string> {
  const out = new Set<string>();
  function walk(list: LocationTreeNode[]) {
    for (const n of list) {
      out.add(String(n.location_id));
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  return out;
}

/** Return a copy of the tree with removed nodes (and their descendants) excluded. */
export function filterTreeRemoved(nodes: LocationTreeNode[], removedIds: Set<string>): LocationTreeNode[] {
  return nodes
    .filter((n) => !removedIds.has(String(n.location_id)))
    .map((n) => ({
      ...n,
      child: filterTreeRemoved(n.child ?? [], removedIds),
    }));
}

/** Deep clone a single node and its children. */
function cloneNode(n: LocationTreeNode): LocationTreeNode {
  return {
    ...n,
    child: (n.child ?? []).map(cloneNode),
  };
}

/** Find a node by id in tree; returns the node and its parent context. */
function findInTree(
  roots: LocationTreeNode[],
  id: string
): { node: LocationTreeNode; parentChildren: LocationTreeNode[]; parentId: string | null; idx: number } | null {
  const idStr = String(id);
  type Result = { node: LocationTreeNode; parentChildren: LocationTreeNode[]; parentId: string | null; idx: number } | null;
  function inList(list: LocationTreeNode[], parentId: string | null): Result {
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].location_id) === idStr) {
        return { node: list[i], parentChildren: list, parentId, idx: i };
      }
      const inChild = inList(list[i].child ?? [], list[i].location_id);
      if (inChild) return inChild;
    }
    return null;
  }
  return inList(roots, null);
}

/** Remove node by id from a mutable copy of the tree (roots). Modifies in place. */
function removeFromTree(roots: LocationTreeNode[], id: string): boolean {
  const idStr = String(id);
  function removeFrom(list: LocationTreeNode[]): boolean {
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].location_id) === idStr) {
        list.splice(i, 1);
        return true;
      }
      if (removeFrom(list[i].child ?? [])) return true;
    }
    return false;
  }
  return removeFrom(roots);
}

/** Insert nodes as children of parentId at index; parentId null = insert into roots. Modifies roots in place. */
function insertAt(
  roots: LocationTreeNode[],
  parentId: string | null,
  index: number,
  toInsert: LocationTreeNode[]
): void {
  if (parentId == null) {
    roots.splice(index, 0, ...toInsert);
    return;
  }
  const found = findInTree(roots, parentId);
  if (!found) return;
  const children = found.node.child ?? [];
  const newChild = [...children];
  newChild.splice(index, 0, ...toInsert);
  found.node.child = newChild;
}

/**
 * Produce a new tree (array of roots) with the given nodes moved to a new parent at index.
 * dragIds order is preserved. Updates parent_location_id and location_level on moved nodes.
 */
export function moveNodesInTree(
  roots: LocationTreeNode[],
  dragIds: string[],
  parentId: string | null,
  index: number
): LocationTreeNode[] {
  const rootsCopy = roots.map(cloneNode);
  const toMove: LocationTreeNode[] = [];
  for (const id of dragIds) {
    const found = findInTree(rootsCopy, id);
    if (!found) continue;
    toMove.push(cloneNode(found.node));
    removeFromTree(rootsCopy, id);
  }
  if (toMove.length === 0) return roots;

  const newLevel = parentId == null ? 1 : (findInTree(rootsCopy, parentId)?.node.location_level ?? 1) + 1;
  const newParentId = parentId ?? null;
  for (const n of toMove) {
    n.parent_location_id = newParentId;
    n.location_level = newLevel;
  }
  insertAt(rootsCopy, parentId, index, toMove);
  return rootsCopy;
}

/**
 * Produce a new tree with the node at nodeId updated (e.g. location_type). Immutable.
 */
export function updateNodeInTree(
  roots: LocationTreeNode[],
  nodeId: string,
  updates: Partial<Pick<LocationTreeNode, 'location_type' | 'name' | 'location_group' | 'meta'>>
): LocationTreeNode[] {
  const idStr = String(nodeId);
  return roots.map((n) => {
    if (String(n.location_id) === idStr) {
      return { ...n, ...updates, child: n.child ?? [] };
    }
    return {
      ...n,
      child: updateNodeInTree(n.child ?? [], nodeId, updates),
    };
  });
}

/** Collect all nodes whose name exactly matches (case-sensitive). */
export function collectNodesByName(nodes: LocationTreeNode[], name: string): LocationTreeNode[] {
  const out: LocationTreeNode[] = [];
  function walk(list: LocationTreeNode[]) {
    for (const n of list) {
      if (n.name === name) out.push(n);
      if (n.child?.length) walk(n.child);
    }
  }
  walk(nodes);
  return out;
}

/**
 * Produce a new tree with every node whose name exactly matches (case-sensitive) updated. Immutable.
 */
export function updateNodesInTreeByName(
  roots: LocationTreeNode[],
  name: string,
  updates: Partial<Pick<LocationTreeNode, 'location_type' | 'name'>>
): LocationTreeNode[] {
  return roots.map((n) => {
    const match = n.name === name;
    return {
      ...n,
      ...(match ? updates : {}),
      child: updateNodesInTreeByName(n.child ?? [], name, updates),
    };
  });
}

/**
 * Move selected siblings under a new parent, folding same name+type ones into what is already
 * there.
 *
 * `mergedAwayIds` lists every node that left the tree by being merged, descendants included. The
 * caller must record those as removed: on a saved tree they are real rows, and a node that is gone
 * from the tree but absent from removedIds stays in the database forever, orphaned and invisible.
 */
export function moveSiblingsWithMerge(
  roots: LocationTreeNode[],
  selectedIds: string[],
  targetParentId: string | null
): { tree: LocationTreeNode[]; idMapping: Record<string, string>; mergedAwayIds: string[] } {
  const selectedIdSet = new Set(selectedIds.map(String));
  if (selectedIdSet.size === 0) return { tree: roots, idMapping: {}, mergedAwayIds: [] };

  const rootsCopy = roots.map(cloneNode);

  // Find parent of first selected node to enforce sibling-only at operation level.
  const firstFound = findInTree(rootsCopy, Array.from(selectedIdSet)[0]);
  if (!firstFound) return { tree: roots, idMapping: {}, mergedAwayIds: [] };
  const parentId = firstFound.parentId;

  // Collect all selected nodes that share this parent.
  const siblingNodes: LocationTreeNode[] = [];
  function collectSiblings(list: LocationTreeNode[], currentParentId: string | null) {
    for (const n of list) {
      const idStr = String(n.location_id);
      if (selectedIdSet.has(idStr) && currentParentId === parentId) {
        siblingNodes.push(n);
      }
      if (n.child?.length) collectSiblings(n.child, n.location_id);
    }
  }
  collectSiblings(rootsCopy, null);
  if (siblingNodes.length === 0) return { tree: roots, idMapping: {}, mergedAwayIds: [] };

  // Remove siblings from their current parent.
  for (const n of siblingNodes) {
    removeFromTree(rootsCopy, String(n.location_id));
  }

  // Find target parent node.
  const targetInfo = targetParentId != null ? findInTree(rootsCopy, targetParentId) : null;
  const targetChildren: LocationTreeNode[] =
    targetInfo?.node.child ?? (targetParentId == null ? rootsCopy : []);
  const targetParentLevel =
    targetParentId == null ? 0 : targetInfo?.node.location_level ?? 0;

  const keyFor = (n: LocationTreeNode) =>
    `${n.name ?? ''}|${(n.location_type ?? '').toString()}`;

  const existingByKey = new Map<string, LocationTreeNode>();
  for (const c of targetChildren) {
    existingByKey.set(keyFor(c), c);
  }

  const idMapping: Record<string, string> = {};
  const mergedAwayIds: string[] = [];

  // For each selected sibling: merge or move.
  for (const original of siblingNodes) {
    const oldId = String(original.location_id);
    const k = keyFor(original);
    const existing = existingByKey.get(k);
    if (existing) {
      // Merge: map old id to the surviving child's id. The node stays out of the tree, so
      // report it - and everything under it - as removed.
      idMapping[oldId] = String(existing.location_id);
      for (const goneId of collectDescendantIds([original])) mergedAwayIds.push(String(goneId));
      continue;
    }
    // Move: re-parent under target and adjust level.
    const moved = cloneNode(original);
    const newParentId = targetParentId ?? null;
    const newLevel = targetParentLevel + 1;
    function updateLevels(node: LocationTreeNode, level: number, parent: string | null) {
      node.parent_location_id = parent;
      node.location_level = level;
      if (node.child?.length) {
        for (const child of node.child) {
          updateLevels(child, level + 1, String(node.location_id));
        }
      }
    }
    updateLevels(moved, newLevel, newParentId);
    targetChildren.push(moved);
    existingByKey.set(k, moved);
    idMapping[oldId] = oldId;
  }

  return { tree: rootsCopy, idMapping, mergedAwayIds };
}

/** Total nodes in a tree, for "built N locations" style messages. */
export function countTreeNodes(nodes: LocationTreeNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countTreeNodes(node.child ?? []), 0);
}
